<template>
  <Box :close-on-click-outside="true" :handleClose="closeDialog">
    <div class="dialog" @keydown.esc="() => closeDialog(null)">
      <div class="header has-bcc-background has-text-white">
        <span>Claim Status Dashboard</span>
      </div>

      <div class="body">
        <div class="filter-bar">
          <div class="field is-grouped is-grouped-multiline">
            <div class="control">
              <label class="label is-small">Status</label>
              <SearchableDropdown :options="statusOptions" :defaultItem="statusOptions[getStatusOptionIndex]" :isDefault="false" @selected="onStatusSelected" />
            </div>
          </div>
        </div>

        <DataGrid
          class="ag-theme-alpine"
          :columnDefs="columnDefs"
          :rowData="deathClaimsData"
          :gridOptions="gridOptions"
          :defaultColDef="defaultColDef"
          :loading="isLoading"
          :pagination="false"
          :enableCellTextSelection="true"
          @grid-ready="onGridReady"
        />

        <small style="color: #999">{{ deathClaimsData.length }} record{{ deathClaimsData.length !== 1 ? 's' : '' }}</small>
      </div>

      <div class="actions">
        <div class="action-buttons">
          <button type="button" class="button" @click="closeDialog(null)">Close</button>
        </div>
      </div>
    </div>
  </Box>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount } from 'vue'
import Box from '../dialogs/Box.vue'
import SearchableDropdown from '../partials/dropdowns/SearchableDropdown.vue'
import { closeDialog } from 'vue3-promise-dialog'
import moment from 'moment'
import { handleStatusString, formatDate } from './helpers'
import { store } from '../../legacyStore'
import useApi from '../../composables/useApi'

const statusOptions = [
  { id: '0', name: 'All' },
  { id: '2', name: 'Open' },
  { id: '1', name: 'Closed' },
]

const getStatusOptionIndex = computed(() => {
  return statusOptions.findIndex((option) => option.id === props.status)
})

const props = defineProps({
  status: {
    type: String,
    default: '0',
  },
})

const deathClaimsData = ref([])
const filterStatus = ref('2')
const isLoading = ref(false)
const gridApi = ref(null)
const clientSession = store.state.clientSession
const api = useApi()

const gridHeight = computed(() => {
  const windowHeight = window.innerHeight
  const height = windowHeight - 200
  return `${height}px`
})

const defaultColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  floatingFilter: true,
}

const columnDefs = computed(() => [
  // {
  //   headerName: 'Case #',
  //   field: 'Title',
  //   width: 130,
  //   pinned: 'left',
  // },
  {
    headerName: 'Insured',
    field: 'Insured',
    width: 260,
  },
  {
    headerName: 'Status',
    field: 'DeathClaimStatus',
    width: 470,
    valueFormatter: (p) => handleStatusString(p, false).text,
  },
  {
    headerName: 'Date',
    field: 'DeathClaimStatus',
    width: 180,
    filter: 'agDateColumnFilter',
    filterParams: { includeTime: false },
    sort: 'desc',
    comparator: (valueA, valueB, nodeA, nodeB) => {
      const dateA = handleStatusString({ value: valueA, data: nodeA.data }, false).date
      const dateB = handleStatusString({ value: valueB, data: nodeB.data }, false).date
      if (!dateA && !dateB) return 0
      if (!dateA) return -1
      if (!dateB) return 1
      return new Date(dateA) - new Date(dateB)
    },
    valueFormatter: (p) => (handleStatusString(p, false).date ? formatDate(handleStatusString(p, false).date) : ''),
  },
  {
    headerName: 'Carrier',
    field: 'Carrier',
    width: 350,
  },
  {
    headerName: 'Policy #',
    field: 'PolicyNumber',
    width: 130,
  },
  {
    headerName: 'Split Dollar',
    field: 'SplitDollarPlan',
    width: 135,
    valueFormatter: (p) => (p.value === 'True' ? 'Yes' : p.value === 'False' ? 'No' : ''),
  },
  {
    headerName: 'Policy Effective',
    field: 'PolicyEffectiveDate',
    width: 180,
    filter: 'agDateColumnFilter',
    filterParams: { includeTime: false },
    valueFormatter: (p) => formatDate(p.value),
  },
  {
    headerName: 'Notes',
    field: 'Notes',
    width: 500,
  },
])

const gridOptions = {
  theme: 'legacy',
  suppressMenuHide: true,
  enableBrowserTooltips: true,
  rowHeight: 35,
  headerHeight: 40,
  floatingFiltersHeight: 36,
  animateRows: true,
  suppressCellFocus: true,
  suppressRowTransform: true,
  getRowId: (params) => String(params.data.Id),
}

function onGridReady(params) {
  gridApi.value = params.api
  handleGrid()
}

const handleGrid = () => {
  setTimeout(() => {
    if (gridApi.value && typeof gridApi.value.sizeColumnsToFit === 'function' && !(gridApi.value.isDestroyed && gridApi.value.isDestroyed())) {
      // gridApi.value.sizeColumnsToFit()
      gridApi.value.refreshCells({ force: true })
    }
  }, 250)
}

function onStatusSelected(option) {
  filterStatus.value = option.id
}

async function loadData() {
  isLoading.value = true
  try {
    deathClaimsData.value = await api.getDeathClaimsData(clientSession.institutionId, filterStatus.value)
  } finally {
    isLoading.value = false
  }
}

watch(filterStatus, loadData)

onBeforeMount(() => {
  filterStatus.value = props.status
  loadData()
})
</script>

<style scoped lang="scss">
@import '../dialogs/dialog.scss';

.dialog {
  min-width: 98vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
}

.body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-bar {
  padding: 10px 0 0;

  .label {
    margin-bottom: 4px;
  }
}

:deep(.ag-theme-alpine) {
  height: v-bind(gridHeight) !important;
}

:deep(.ag-root-wrapper) {
  border-radius: 3px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ddd;
  padding: 15px;
  margin-top: 15px;

  .action-buttons {
    display: flex;
    gap: 10px;
  }
}
</style>
